import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Card, Divider, MenuItem, TextField } from '@mui/material';
import PageHeader from '../common/PageHeader';
import DayBookReport from './DayBookReport';
import BrandWiseReport from './BrandWiseReport';
import StockReport from './StockReport';
import StockAlertReport from './StockAlertReport';
import DeadStockReport from './DeadStockReport';
import StaffReport from './StaffReport';
import GstReport from './GstReport';
import EmiReport from './EmiReport';

type ReportKey = 'DAY_BOOK' | 'BRAND_WISE' | 'STOCK' | 'LOW_STOCK' | 'DEAD_STOCK' | 'EMI' | 'GST' | 'STAFF';

/** The reports the shop can run. Each brings its own filters with it. */
const REPORTS: { key: ReportKey; label: string; element: JSX.Element }[] = [
  { key: 'DAY_BOOK', label: 'Day Book - Cash, UPI, Credit, EMI', element: <DayBookReport /> },
  { key: 'BRAND_WISE', label: 'Brand-wise Stock & Sales', element: <BrandWiseReport /> },
  { key: 'STOCK', label: 'Stock & Serial Numbers', element: <StockReport /> },
  { key: 'LOW_STOCK', label: 'Low Stock - what to order', element: <StockAlertReport /> },
  { key: 'DEAD_STOCK', label: 'Dead Stock - not moving', element: <DeadStockReport /> },
  { key: 'EMI', label: 'EMI Report', element: <EmiReport /> },
  { key: 'GST', label: 'GST Summary', element: <GstReport /> },
  { key: 'STAFF', label: 'Staff & Discounts', element: <StaffReport /> },
];

/**
 * Reports, chosen from a list rather than opened from tabs.
 *
 * <p>Tabs put every report on screen at once and still only show one - and they run
 * out of room the moment a fourth is added. Picking the type reads the way the shop
 * asks for it: "show me the EMI report".
 */
const Reports = () => {
  /*
   * Opens on whichever report the link asked for, so the dashboard can point straight
   * at the answer: "3 products out of stock" goes to the low stock report rather than
   * leaving the owner to find it.
   *
   * Anything unrecognised falls through to the day book, which is the one the shop
   * opens most anyway.
   */
  const [params] = useSearchParams();
  const asked = params.get('report')?.toUpperCase();
  const opening = REPORTS.some((item) => item.key === asked) ? (asked as ReportKey) : 'DAY_BOOK';

  const [selected, setSelected] = useState<ReportKey>(opening);

  /*
   * Follows the address, not only the first render.
   *
   * Read once at startup, the screen stayed on whatever it opened with: going back
   * from the dead stock report to the low stock one changed the address and left the
   * page showing dead stock, because this component never unmounted in between. The
   * browser's own back button looked broken.
   */
  useEffect(() => {
    setSelected(opening);
  }, [opening]);

  const report = REPORTS.find((item) => item.key === selected) ?? REPORTS[0];

  return (
    <Box>
      <PageHeader title="Reports" />

      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <TextField
          select
          label="Report Type"
          size="small"
          sx={{ minWidth: 280 }}
          value={selected}
          onChange={(event) => setSelected(event.target.value as ReportKey)}
        >
          {REPORTS.map((item) => (
            <MenuItem key={item.key} value={item.key}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <Divider sx={{ my: 2 }} />

        {/* Keyed, so switching report starts the new one clean rather than
            carrying the last one's filters into it. */}
        <Box key={report.key}>{report.element}</Box>
      </Card>
    </Box>
  );
};

export default Reports;
